// DRUM carousel: shows one slide at a time; prev/next buttons, numbered dots, arrow keys.
class DrumCarousel extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-drum-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-drum-dot]'));
    this.status = this.querySelector('[data-drum-status]');
    if (this.slides.length < 2) return;

    this.index = 0;
    this.querySelector('[data-drum-prev]')?.addEventListener('click', () => this.go(this.index - 1));
    this.querySelector('[data-drum-next]')?.addEventListener('click', () => this.go(this.index + 1));
    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i)));
    this.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') this.go(this.index - 1);
      if (event.key === 'ArrowRight') this.go(this.index + 1);
    });
    this.go(0, false);
  }

  go(i, announce = true) {
    const count = this.slides.length;
    this.index = (i + count) % count;
    this.slides.forEach((slide, n) => {
      const active = n === this.index;
      slide.classList.toggle('is-active', active);
      slide.toggleAttribute('inert', !active);
      slide.setAttribute('aria-hidden', String(!active));
    });
    this.dots.forEach((dot, n) => dot.setAttribute('aria-current', String(n === this.index)));
    if (announce && this.status) this.status.textContent = `Slide ${this.index + 1} of ${count}`;
  }
}

if (!customElements.get('drum-carousel')) customElements.define('drum-carousel', DrumCarousel);

// DRUM poster mosaic: fills the random tiles with posters from a random page of the pool collection.
class DrumMosaic extends HTMLElement {
  connectedCallback() {
    const url = this.dataset.poolUrl;
    const pages = parseInt(this.dataset.poolPages || '1', 10);
    const tiles = Array.from(this.querySelectorAll('[data-mosaic-random]'));
    if (!url || !tiles.length) return;

    const featured = new Set(Array.from(this.querySelectorAll('[data-handle]')).map((tile) => tile.dataset.handle));
    // Collections are sorted (e.g. by title), so mix up to three random pages for variety.
    const pageNumbers = new Set();
    while (pageNumbers.size < Math.min(3, pages)) pageNumbers.add(1 + Math.floor(Math.random() * pages));
    const requests = Array.from(pageNumbers, (page) =>
      fetch(`${url}/products.json?limit=50&page=${page}`).then((response) => (response.ok ? response.json() : { products: [] }))
    );

    Promise.all(requests)
      .then((results) => {
        const products = results.flatMap((result) => result.products);
        const picks = products.filter((product) => product.images.length && !featured.has(product.handle));
        for (let i = picks.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [picks[i], picks[j]] = [picks[j], picks[i]];
        }
        tiles.forEach((tile, i) => {
          const product = picks[i];
          if (product) tile.replaceChildren(this.tileLink(product));
        });
      })
      .catch(() => {}); // keep the server-rendered posters
  }

  tileLink(product) {
    const src = product.images[0].src;
    const sized = (width) => `${src}${src.includes('?') ? '&' : '?'}width=${width}`;
    const link = document.createElement('a');
    link.className = 'drum-mosaic__link';
    link.href = `${window.Shopify?.routes?.root || '/'}products/${product.handle}`;
    const img = document.createElement('img');
    img.src = sized(400);
    img.srcset = `${sized(200)} 200w, ${sized(300)} 300w, ${sized(400)} 400w`;
    img.sizes = '(min-width: 990px) 12vw, 30vw';
    img.alt = product.title;
    img.loading = 'lazy';
    link.append(img);
    return link;
  }
}

if (!customElements.get('drum-mosaic')) customElements.define('drum-mosaic', DrumMosaic);

// DRUM hero headlines: safety net for the size rule in drum-hero.liquid. A headline that still breaks a word,
// or (on computers) makes its slide taller than the image side, is stepped down until it fits.
(() => {
  const SIDE_BY_SIDE = window.matchMedia('(min-width: 990px)');

  function fitHero(hero) {
    const slides = [...hero.querySelectorAll('.drum-hero__slide')];
    const headings = slides.map((slide) => slide.querySelector('.drum-d-hero')).filter(Boolean);
    const track = hero.querySelector('.drum-hero__track');

    // The height the slides have without headline pressure: every headline small, everything else as is
    headings.forEach((heading) => heading.style.setProperty('font-size', '30px', 'important'));
    const limit = SIDE_BY_SIDE.matches ? track.offsetHeight : Infinity;
    headings.forEach((heading) => heading.style.removeProperty('font-size'));

    // With the class on, each panel is its natural height instead of stretching to the tallest slide
    hero.classList.add('drum-hero--measure');
    slides.forEach((slide) => {
      const heading = slide.querySelector('.drum-d-hero');
      const panel = slide.querySelector('.drum-split__panel');
      if (!heading || !panel) return;
      let size = parseFloat(getComputedStyle(heading).fontSize);
      const tooBig = () => heading.scrollWidth > heading.clientWidth + 1 || panel.offsetHeight > limit + 1;
      while (tooBig() && size > 30) {
        size -= 2;
        heading.style.setProperty('font-size', `${size}px`, 'important');
      }
    });
    hero.classList.remove('drum-hero--measure');
  }

  const fitAll = () => document.querySelectorAll('.drum-hero').forEach(fitHero);
  document.fonts?.ready.then(fitAll);
  window.addEventListener('load', fitAll);
  let frame;
  window.addEventListener('resize', () => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(fitAll);
  });
})();
